import { FC, HTMLAttributes } from "react";
import { useStyles } from "./ActivityCard.styles";
import { Typography } from "../../core/Typography/Typography";
import { Image } from "../../core/Image/Image";
import { TypographyVariant } from "../../../constants/typography";
import { Activity } from "@onmo/graphql/graphql";
import { Time } from "../../../models/time";
import LbDaily from "../../../assets/icon/activity/activity_daily_1x.webp";
import LbDaily2x from "../../../assets/icon/activity/activity_daily_2x.webp";
import LbDaily3x from "../../../assets/icon/activity/activity_daily_3x.webp";
import LbMonthly from "../../../assets/icon/activity/activity_monthly_1x.webp";
import LbMonthly2x from "../../../assets/icon/activity/activity_monthly_2x.webp";
import LbMonthly3x from "../../../assets/icon/activity/activity_monthly_3x.webp";
import LbWeekly from "../../../assets/icon/activity/activity_weekly_1x.webp";
import LbWeekly2x from "../../../assets/icon/activity/activity_weekly_2x.webp";
import LbWeekly3x from "../../../assets/icon/activity/activity_weekly_3x.webp";
import { useTranslation } from "react-i18next";

interface IActivityCardProps {
  activity: Activity;
}

export const ActivityCard: FC<
  IActivityCardProps & HTMLAttributes<HTMLDivElement>
> = ({ activity }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const convertActivity = () => {
    const data = activity.data;
    switch (data?.__typename) {
      case "BattleActivityData": {
        const gameData = JSON.parse(data.game.metadata || "{}");
        return {
          title: t("common_Battle"),
          description: data.game.title,
          src1x: gameData?.gameImages?.small,
          src2x: gameData?.gameImages?.small2x,
          src3x: gameData?.gameImages?.small3x,
          createdAt: activity.createdAt,
        };
      }
      case "UserLeaderboardActivityData": {
        if (data.repeatCycle === "daily") {
          return {
            title: `${t("common_Daily")} ${t("common_Leaderboard")}`,
            description: "#" + data.leaderboardRank,
            src1x: LbDaily,
            src2x: LbDaily2x,
            src3x: LbDaily3x,
            createdAt: activity.createdAt,
          };
        } else if (data.repeatCycle === "weekly") {
          return {
            title: `${t("common_Weekly")} ${t("common_Leaderboard")}`,
            description: "#" + data.leaderboardRank,
            src1x: LbWeekly,
            src2x: LbWeekly2x,
            src3x: LbWeekly3x,
            createdAt: activity.createdAt,
          };
        } else {
          return {
            title: `${t("common_Monthly")} ${t("common_Leaderboard")}`,
            description: "#" + data.leaderboardRank,
            src1x: LbMonthly,
            src2x: LbMonthly2x,
            src3x: LbMonthly3x,
            createdAt: activity.createdAt,
          };
        }
      }
      default:
        return null;
    }
  };

  if (!convertActivity()) {
    return null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <Image
          className={classes.image}
          url={convertActivity()?.src1x}
          url2x={convertActivity()?.src2x}
          url3x={convertActivity()?.src3x}
        />
        <div className={classes.contentText}>
          <Typography variant={TypographyVariant.BodyB5Bold}>
            {convertActivity()?.title}
          </Typography>
          <Typography variant={TypographyVariant.BodyB6Medium}>
            {convertActivity()?.description}
          </Typography>
        </div>
      </div>
      <Typography variant={TypographyVariant.BodyB6Regular}>
        {Time.convertTimestampToFormattedDate(
          convertActivity()?.createdAt,
          true,
        )}
      </Typography>
    </div>
  );
};
